<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <p class="control" style="padding-top: 0.5rem;">
              <span class="subtitle">
                {{ $t('attributes.gift_posts') }}
              </span>
            </p>
          </b-field>

          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.year') }}
              </span>
            </p>
            <b-select v-model="filterYear" @input="onFilterYear">
              <option value="">{{ $t('attributes.all') }}</option>
              <option v-for="year in years" :key="year" :value="year">
                {{ year }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.month') }}
              </span>
            </p>
            <b-select
              v-model="filterMonth"
              :disabled="!filterYear"
              @input="onFilterMonth"
            >
              <option value="">{{ $t('attributes.all') }}</option>
              <option
                v-for="(month, index) in months"
                :key="month"
                :value="index + 1"
              >
                {{ $t(`attributes.${month}`) }}
              </option>
            </b-select>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop"></div>
    </nav>

    <b-table
      v-if="!filterMonth"
      :data="yearPosts"
      :loading="loadingItems"
      :mobile-cards="false"
      scrollable
      hoverable
      narrowed
      sticky-header
      height="450"
    >
      <b-table-column
        field="gift_name"
        :label="$t('attributes.gift_name')"
        width="120"
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ props.row.gift_name }}
      </b-table-column>

      <b-table-column
        field="department_no"
        :label="$t('attributes.gift_department')"
        width="120"
        header-class="nowrap"
        v-slot="props"
      >
        {{
          !!props.row.gift_department_no
            ? `[${props.row.gift_department_no}] ${props.row.gift_department_name}`
            : '總公司'
        }}
      </b-table-column>

      <b-table-column
        field="year"
        :visible="!filterYear"
        :label="$t('attributes.year')"
        width="80"
        numeric
        header-class="nowrap"
        v-slot="props"
      >
        {{ props.row.year }}
      </b-table-column>

      <b-table-column
        v-for="(month, idx) in months"
        :key="idx"
        :label="$t('attributes.' + month)"
        width="80"
        numeric
        header-class="nowrap"
        v-slot="props"
      >
        {{ props.row.months[idx] || '-' }}
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-table
      v-else
      :data="monthPosts"
      :loading="loadingItems || updatingMonthPosts"
      :mobile-cards="false"
      scrollable
      hoverable
      narrowed
      sticky-header
      height="360px"
    >
      <b-table-column
        field="gift_name"
        :label="$t('attributes.gift_name')"
        width="100"
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ props.row.gift_name }}
      </b-table-column>

      <b-table-column
        field="department_no"
        :label="$t('attributes.gift_department')"
        width="100"
        header-class="nowrap"
        v-slot="props"
      >
        {{
          !!props.row.gift_department_no
            ? `[${props.row.gift_department_no}] ${props.row.gift_department_name}`
            : '總公司'
        }}
      </b-table-column>

      <b-table-column
        field="init_quantity"
        :label="$t('attributes.init_quantity')"
        width="80"
        numeric
        v-slot="props"
      >
        {{ props.row.init_quantity || '-' }}
      </b-table-column>

      <b-table-column
        field="init_average_cost"
        :label="$t('attributes.init_average_cost')"
        width="90"
        numeric
        v-slot="props"
      >
        {{ props.row.init_average_cost || '-' }}
      </b-table-column>

      <b-table-column
        field="init_cost"
        :label="$t('attributes.init_cost')"
        width="80"
        numeric
        v-slot="props"
      >
        {{ props.row.init_cost || '-' }}
      </b-table-column>

      <b-table-column
        field="purchase_quantity"
        :label="$t('attributes.purchase_quantity')"
        width="80"
        numeric
        v-slot="props"
      >
        {{ props.row.purchase_quantity || '-' }}
      </b-table-column>

      <b-table-column
        field="purchase_cost"
        :label="$t('attributes.purchase_cost')"
        width="80"
        numeric
        v-slot="props"
      >
        {{ props.row.purchase_cost || '-' }}
      </b-table-column>

      <b-table-column
        field="adjust_quantity"
        :label="$t('attributes.adjust_quantity')"
        width="80"
        numeric
        v-slot="props"
      >
        {{ props.row.adjust_quantity || '-' }}
      </b-table-column>

      <b-table-column
        field="adjust_cost"
        :label="$t('attributes.adjust_cost')"
        width="80"
        numeric
        v-slot="props"
      >
        {{ props.row.adjust_cost || '-' }}
      </b-table-column>

      <b-table-column
        field="sales_quantity"
        :label="$t('attributes.sales_quantity')"
        width="80"
        numeric
        v-slot="props"
      >
        {{ props.row.sales_quantity || '-' }}
      </b-table-column>

      <b-table-column
        field="sales_cost"
        :label="$t('attributes.sales_cost')"
        width="80"
        numeric
        v-slot="props"
      >
        {{ props.row.sales_cost || '-' }}
      </b-table-column>

      <b-table-column
        field="sales_amount"
        :label="$t('attributes.sales_amount')"
        width="80"
        numeric
        v-slot="props"
      >
        {{ props.row.sales_amount || '-' }}
      </b-table-column>

      <b-table-column
        field="sales_profit"
        :label="$t('attributes.sales_profit')"
        width="80"
        numeric
        v-slot="props"
      >
        {{ props.row.sales_profit || '-' }}
      </b-table-column>

      <b-table-column
        field="final_quantity"
        :label="$t('attributes.final_quantity')"
        width="80"
        numeric
        v-slot="props"
      >
        {{ props.row.final_quantity || '-' }}
      </b-table-column>

      <b-table-column
        field="average_cost"
        :label="$t('attributes.average_cost')"
        width="90"
        numeric
        v-slot="props"
      >
        {{ props.row.average_cost || '-' }}
      </b-table-column>

      <b-table-column
        field="final_cost"
        :label="$t('attributes.final_cost')"
        width="80"
        numeric
        v-slot="props"
      >
        {{ props.row.final_cost || '-' }}
      </b-table-column>
    </b-table>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { mapGetters } from 'vuex'
import { GIFT_POSTS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'GiftPostList',

  props: {
    gifts: {
      type: Array,
      required: true
    }
  },
  data() {
    const startYear = 2021
    const currentYear = new Date().getFullYear()
    const years = [...Array(currentYear - startYear + 1).keys()].map(
      x => currentYear - x
    )

    return {
      loadingItems: false,
      updatingMonthPosts: false,
      posts: [],
      yearPosts: [],
      monthPosts: [],

      years: years,
      months: [
        'january',
        'february',
        'march',
        'april',
        'may',
        'june',
        'july',
        'august',
        'september',
        'october',
        'november',
        'december'
      ],

      filterYear: currentYear,
      filterMonth: ''
    }
  },
  watch: {
    gifts(items) {
      this.loadItems(items)
    }
  },
  computed: {
    ...mapGetters('app', ['hasPermission'])
  },
  mounted() {
    this.loadItems(this.gifts)
  },
  methods: {
    async refresh() {
      await this.loadItems(this.gifts)
    },
    async loadItems(gifts) {
      try {
        const api = `${GIFT_POSTS_API}`
        const params = {
          gifts: gifts.map(g => g.id).join(','),
          year: this.filterYear
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        this.posts = res.data.data

        this.updateYearPosts()
        this.updateMonthPosts()
      } catch (e) {
        this.yearPosts = []

        notifyService.fetch_error('gift_posts', e)
      } finally {
        this.loadingItems = false
      }
    },
    updateYearPosts() {
      const years = this.filterYear ? [this.filterYear] : this.years
      const months = [...Array(12).keys()].map(x => x + 1)

      this.yearPosts = this.gifts.reduce((list, g) => {
        const yearPosts = years.map(y => {
          const monthPosts = this.posts.filter(
            p => p.gift_id === g.id && p.year === y
          )

          return {
            gift_name: g.name,
            gift_department_no: g.department_no,
            gift_department_name: g.department_name,
            year: y,
            months: months.map(m => {
              const p = monthPosts.find(p => p.month === m)
              return p ? p.init_quantity : null
            })
          }
        })

        return [...list, ...yearPosts]
      }, [])
    },
    updateMonthPosts() {
      this.updatingMonthPosts = true
      if (!this.filterYear || !this.filterMonth) {
        this.monthPosts = []
      } else {
        this.monthPosts = this.gifts.map(g => {
          const post = this.posts.find(
            p =>
              p.gift_id === g.id &&
              p.year === this.filterYear &&
              p.month === this.filterMonth
          )

          if (post) {
            return {
              ...post,
              gift_name: g.name,
              gift_department_no: g.department_no,
              gift_department_name: g.department_name
            }
          } else {
            return {
              gift_name: g.name,
              gift_department_no: g.department_no,
              gift_department_name: g.department_name,
              year: this.filterYear,
              month: this.filterMonth
            }
          }
        })
        this.updatingMonthPosts = false
      }
    },

    onFilterYear: debounce(function() {
      this.filterMonth = ''
      this.loadItems(this.gifts)
    }, 500),
    onFilterMonth: debounce(function() {
      this.updateMonthPosts()
    }, 500)
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
